import { AcademicCapIcon } from "@heroicons/react/24/outline";
// Namespace import + runtime pick: @mindstonehq/ui ships TSX source whose
// named exports are not statically analyzable under the node/tsx ESM loader,
// which the self-running component test uses. Vite resolves either form.
import * as UI from "@mindstonehq/ui";
import type { CommunityVerifiedLearningSignal } from "~/lib/community-signals-types";
import { formatEventMeta } from "~/lib/profile-celebration";

const ui = (UI as typeof UI & { default?: typeof UI }).Badge
  ? UI
  : ((UI as typeof UI & { default?: typeof UI }).default ?? UI);
const { Badge, Card, CardContent, CardDescription, CardHeader, CardTitle } = ui;

const VISIBLE_TOPIC_COUNT = 3;

function verifiedTalkLabel(count: number): string {
  return count === 1 ? "talk" : "talks";
}

export default function ProfileVerifiedLearning({
  verifiedLearning,
  isCurrentUser,
}: {
  verifiedLearning?: CommunityVerifiedLearningSignal;
  isCurrentUser: boolean;
}) {
  if (!verifiedLearning) return null;

  const moreCount = Math.max(
    0,
    verifiedLearning.count - verifiedLearning.items.length,
  );

  return (
    <Card id="profile-verified-learning">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <AcademicCapIcon className="size-5 text-muted-foreground" />
          Knowledge verified
        </CardTitle>
        <CardDescription>
          Short checks after specific Mindstone talks, not a proctored
          credential.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <p className="text-sm text-muted-foreground">
          Comprehension checked for{" "}
          <span className="font-semibold text-foreground">
            {verifiedLearning.count}
          </span>{" "}
          {verifiedTalkLabel(verifiedLearning.count)}
        </p>
        {verifiedLearning.items.length > 0 && (
          <ul className="flex flex-col gap-3">
            {verifiedLearning.items.map((item, i) => {
              const topics = item.topics ?? [];
              const visibleTopics = topics.slice(0, VISIBLE_TOPIC_COUNT);
              const hiddenTopicCount = topics.length - visibleTopics.length;
              const dateMeta = formatEventMeta(null, item.date);
              return (
                <li
                  key={`${item.talkTitle}:${item.date}:${i}`}
                  className="flex flex-col gap-1"
                >
                  <span className="text-sm text-foreground">
                    {item.talkTitle}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {item.eventName}
                    {dateMeta && <>, {dateMeta}</>}
                  </span>
                  {visibleTopics.length > 0 && (
                    <div className="flex flex-wrap items-center gap-1">
                      {visibleTopics.map((topic) => (
                        <Badge
                          key={topic}
                          variant="outline"
                          className="text-xs font-normal"
                        >
                          {topic}
                        </Badge>
                      ))}
                      {hiddenTopicCount > 0 && (
                        <span className="text-xs text-muted-foreground">
                          +{hiddenTopicCount}
                        </span>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
        {moreCount > 0 && (
          <p className="text-xs text-muted-foreground">
            +{moreCount} more verified {verifiedTalkLabel(moreCount)}
          </p>
        )}
        {isCurrentUser && (
          <p className="text-xs text-muted-foreground">
            Knowledge verified only shows talks you passed; answers and scores
            stay private.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
